// ButcherShop — friendly cut-identification UI for kids.
//
// Pedagogy: kid shops at the butcher, learns where cuts come from
// and what they're called (including the "wow" names: guanciale,
// filet mignon, prosciutto). NOT a slaughter UI — the animal is
// alive and smiling. Cuts are color-coded REGIONS of the pig
// silhouette, like a kids' coloring-book diagram.
//
// Tap a region → modal with cut name + alternate name + fun fact +
// the technique combo to actually MAKE that cut in Craft.
//
// EMOJI-IN-UI EXCEPTION: this tree is the explicit opt-in.

import { useState } from "react";
import { COLOR, FONT, RADIUS } from "../../lib/tokens";
import { BUTCHER_CUTS, BUTCHER_ANIMALS, butcherFor } from "./butcherData";

export function ButcherShop({ onClose }) {
  const [animalKey, setAnimalKey] = useState("pig");
  const [activeCut, setActiveCut] = useState(null);
  const animal = butcherFor(animalKey);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: COLOR.bg,
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "14px 16px 10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: COLOR.paper,
          borderBottom: `1px solid ${COLOR.border}`,
          position: "sticky",
          top: 0,
          zIndex: 2,
        }}
      >
        <div>
          <div
            style={{
              fontFamily: FONT.mono,
              fontSize: 10,
              letterSpacing: "0.12em",
              color: COLOR.sub,
              fontWeight: 700,
            }}
          >
            BOODA'S
          </div>
          <div
            style={{
              fontFamily: FONT.detail,
              fontStyle: "italic",
              fontSize: 22,
              fontWeight: 600,
              color: COLOR.ink,
              lineHeight: 1,
            }}
          >
            Butcher Shop
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="mcm-focusable"
          style={{
            all: "unset",
            cursor: "pointer",
            padding: "8px 14px",
            background: "transparent",
            border: `1.5px solid ${COLOR.border}`,
            borderRadius: 10,
            fontFamily: FONT.mono,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.08em",
            color: COLOR.ink,
            touchAction: "manipulation",
          }}
        >
          CLOSE
        </button>
      </div>

      {/* Animal picker */}
      <div
        style={{
          display: "flex",
          gap: 8,
          padding: "10px 16px",
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
          flexShrink: 0,
        }}
      >
        {BUTCHER_ANIMALS.map((key) => {
          const a = BUTCHER_CUTS[key];
          const active = key === animalKey;
          return (
            <button
              key={key}
              type="button"
              onClick={() => {
                setAnimalKey(key);
                setActiveCut(null);
              }}
              className="mcm-focusable"
              style={{
                all: "unset",
                cursor: "pointer",
                flexShrink: 0,
                padding: "8px 14px",
                background: active ? COLOR.goldDeep : "#fff",
                border: `1.5px solid ${active ? COLOR.gold : COLOR.border}`,
                borderRadius: 12,
                display: "flex",
                alignItems: "center",
                gap: 6,
                touchAction: "manipulation",
              }}
            >
              <span style={{ fontSize: 22, lineHeight: 1 }}>{a.animalEmoji}</span>
              <span
                style={{
                  fontFamily: FONT.mono,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  color: active ? COLOR.gold : COLOR.ink,
                }}
              >
                {a.animalName.toUpperCase()}
              </span>
            </button>
          );
        })}
      </div>

      {/* Diagram */}
      <div style={{ padding: "8px 16px 4px" }}>
        <AnimalDiagram
          animalKey={animalKey}
          cuts={animal.cuts}
          onPickCut={setActiveCut}
        />
      </div>

      {/* Hint */}
      <div
        style={{
          padding: "0 16px 12px",
          textAlign: "center",
          fontFamily: FONT.detail,
          fontStyle: "italic",
          fontSize: 14,
          color: COLOR.sub,
        }}
      >
        Tap any colored part to learn its name.
      </div>

      {/* Cut list (also tappable, in case the diagram is small) */}
      <div style={{ padding: "0 12px 24px" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
            gap: 8,
          }}
        >
          {animal.cuts.map((cut) => (
            <button
              key={cut.id}
              type="button"
              onClick={() => setActiveCut(cut)}
              className="mcm-focusable"
              style={{
                all: "unset",
                cursor: "pointer",
                padding: "10px 12px",
                background: cut.color,
                border: `1.5px solid ${COLOR.border}`,
                borderRadius: 12,
                touchAction: "manipulation",
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <div
                style={{
                  fontFamily: FONT.mono,
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  color: "rgba(0,0,0,0.6)",
                }}
              >
                {cut.displayName.toUpperCase()}
              </div>
              <div
                style={{
                  fontFamily: FONT.detail,
                  fontStyle: "italic",
                  fontSize: 15,
                  fontWeight: 600,
                  color: COLOR.ink,
                  lineHeight: 1.1,
                }}
              >
                {cut.alternateName}
              </div>
            </button>
          ))}
        </div>
      </div>

      {activeCut && (
        <CutDetailSheet
          cut={activeCut}
          animal={animal}
          onClose={() => setActiveCut(null)}
        />
      )}
    </div>
  );
}

function AnimalDiagram({ animalKey, cuts, onPickCut }) {
  // A stylized profile silhouette with color-coded clickable
  // regions. Each animal has its own layout; for now we share a
  // "horizontal mammal" template that works for pig/cow/sheep, and
  // a special chicken layout.
  if (animalKey === "hen") return <ChickenDiagram cuts={cuts} onPick={onPickCut} />;
  return <MammalDiagram cuts={cuts} animalEmoji={BUTCHER_CUTS[animalKey].animalEmoji} onPick={onPickCut} />;
}

function MammalDiagram({ cuts, animalEmoji, onPick }) {
  // Layout idea: 5-6 colored rounded rectangles arranged as a
  // mammal in left profile. The cuts are positioned along the
  // body in roughly anatomical order:
  //   [JOWL/CHEEK][SHOULDER/CHUCK][LOIN/RIB][BELLY/BRISKET][HAM/ROUND][FLANK?]
  // We map by cut.id to the correct slot.

  const slotByOrder = ["jowl", "chuck", "shoulder", "rib", "loin", "belly", "brisket", "round", "ham", "flank", "ribs"];
  const ordered = slotByOrder
    .map((id) => cuts.find((c) => c.id === id))
    .filter(Boolean);

  return (
    <div
      style={{
        position: "relative",
        maxWidth: 420,
        margin: "0 auto",
        aspectRatio: "2 / 1",
        background: "#fff",
        border: `1.5px solid ${COLOR.border}`,
        borderRadius: 18,
        padding: 12,
        overflow: "hidden",
      }}
    >
      {/* Animal face on the left — smiling, alive */}
      <div
        style={{
          position: "absolute",
          left: 12,
          top: "50%",
          transform: "translateY(-50%)",
          width: 56,
          height: 56,
          borderRadius: "50%",
          background: ordered[0]?.color || "#FFB5A7",
          border: `2px solid ${COLOR.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 30,
          cursor: ordered[0] ? "pointer" : "default",
          zIndex: 2,
        }}
        onClick={() => ordered[0] && onPick(ordered[0])}
      >
        {animalEmoji}
      </div>

      {/* Body — horizontal strip of colored cut zones */}
      <div
        style={{
          position: "absolute",
          left: 64,
          right: 16,
          top: "30%",
          bottom: "30%",
          display: "flex",
          gap: 2,
          borderRadius: 12,
          overflow: "hidden",
          border: `1.5px solid ${COLOR.border}`,
        }}
      >
        {ordered.slice(1).map((cut) => (
          <button
            key={cut.id}
            type="button"
            onClick={() => onPick(cut)}
            className="mcm-focusable"
            style={{
              all: "unset",
              cursor: "pointer",
              flex: 1,
              background: cut.color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              touchAction: "manipulation",
              padding: 4,
            }}
          >
            <span
              style={{
                fontFamily: FONT.mono,
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: "0.06em",
                color: "rgba(0,0,0,0.7)",
                textAlign: "center",
                lineHeight: 1.1,
              }}
            >
              {cut.displayName.toUpperCase()}
            </span>
          </button>
        ))}
      </div>

      {/* Decorative legs (non-clickable) — 4 small grey rectangles */}
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            bottom: 8,
            left: `${20 + i * 22}%`,
            width: 12,
            height: 28,
            background: "rgba(0,0,0,0.15)",
            borderRadius: 4,
          }}
        />
      ))}
    </div>
  );
}

function ChickenDiagram({ cuts, onPick }) {
  // Chickens are different — they're vertical, with parts spread
  // around a central body. Layout: breast in center, wing on side,
  // thigh below, drumstick lower, whole as a tappable surround.
  const byId = Object.fromEntries(cuts.map((c) => [c.id, c]));
  return (
    <div
      style={{
        position: "relative",
        maxWidth: 320,
        margin: "0 auto",
        aspectRatio: "1 / 1",
        background: "#fff",
        border: `1.5px solid ${COLOR.border}`,
        borderRadius: 18,
        padding: 12,
        overflow: "hidden",
      }}
    >
      {/* Body grid: breast / wing / thigh / drumstick */}
      <div
        style={{
          position: "absolute",
          inset: 12,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gridTemplateRows: "1fr 1fr",
          gap: 6,
        }}
      >
        {["breast", "wing", "thigh", "drumstick"].map((id) => {
          const cut = byId[id];
          if (!cut) return <div key={id} />;
          return (
            <button
              key={id}
              type="button"
              onClick={() => onPick(cut)}
              className="mcm-focusable"
              style={{
                all: "unset",
                cursor: "pointer",
                background: cut.color,
                borderRadius: 12,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: FONT.mono,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.08em",
                color: "rgba(0,0,0,0.7)",
                textAlign: "center",
                touchAction: "manipulation",
                padding: 6,
              }}
            >
              {cut.displayName.toUpperCase()}
            </button>
          );
        })}
      </div>
      {/* Big emoji as a centered backdrop */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: byId.whole ? "auto" : "none",
          cursor: byId.whole ? "pointer" : "default",
          fontSize: 64,
          opacity: 0.35,
        }}
        onClick={() => byId.whole && onPick(byId.whole)}
      >
        🐔
      </div>
    </div>
  );
}

function CutDetailSheet({ cut, animal, onClose }) {
  const combo = cut.howToMake;
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        zIndex: 1100,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 480,
          background: COLOR.paper,
          borderRadius: "18px 18px 0 0",
          padding: 22,
          maxHeight: "85vh",
          overflowY: "auto",
        }}
      >
        {/* Cut header */}
        <div
          style={{
            background: cut.color,
            borderRadius: 14,
            padding: "16px 18px",
            marginBottom: 16,
          }}
        >
          <div
            style={{
              fontFamily: FONT.mono,
              fontSize: 10,
              letterSpacing: "0.12em",
              color: "rgba(0,0,0,0.55)",
              fontWeight: 700,
            }}
          >
            {animal.animalName.toUpperCase()} → {cut.displayName.toUpperCase()}
          </div>
          <div
            style={{
              fontFamily: FONT.detail,
              fontStyle: "italic",
              fontSize: 28,
              fontWeight: 600,
              color: COLOR.ink,
              lineHeight: 1.1,
              marginTop: 4,
            }}
          >
            {cut.alternateName}
          </div>
        </div>

        {/* Fun fact */}
        <div
          style={{
            fontFamily: FONT.detail,
            fontSize: 15,
            lineHeight: 1.5,
            color: COLOR.ink,
            marginBottom: 18,
          }}
        >
          {cut.funFact}
        </div>

        {/* How to make it in Craft */}
        {combo && (
          <div
            style={{
              padding: 14,
              background: "rgba(255,255,255,0.7)",
              border: `1.5px dashed ${COLOR.goldDim}`,
              borderRadius: 12,
              marginBottom: 14,
            }}
          >
            <div
              style={{
                fontFamily: FONT.mono,
                fontSize: 9,
                letterSpacing: "0.1em",
                color: COLOR.sub,
                fontWeight: 700,
                marginBottom: 6,
              }}
            >
              MAKE IT IN CRAFT
            </div>
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              {combo.emojis.map((e, i) => (
                <span
                  key={i}
                  style={{
                    fontSize: 26,
                    lineHeight: 1,
                  }}
                >
                  {i > 0 ? <span style={{ fontSize: 16, color: COLOR.sub, marginRight: 6 }}>+</span> : null}
                  {e}
                </span>
              ))}
              <span
                style={{
                  fontFamily: FONT.mono,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  color: COLOR.sub,
                  marginLeft: "auto",
                }}
              >
                ({combo.technique.toUpperCase()})
              </span>
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={onClose}
          className="mcm-focusable"
          style={{
            all: "unset",
            cursor: "pointer",
            display: "block",
            width: "100%",
            padding: "12px 16px",
            background: COLOR.leaf,
            color: "#fff",
            borderRadius: 12,
            fontFamily: FONT.mono,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.1em",
            textAlign: "center",
            touchAction: "manipulation",
            boxShadow: `0 4px 12px ${COLOR.leafDim}`,
          }}
        >
          GOT IT!
        </button>
      </div>
    </div>
  );
}

export default ButcherShop;
